import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { jobRestoreApi, RemovedJob } from '../../utils/api/jobrestore';
import { fmtDateTime, fmtNumber, moneyTight } from '../../utils/region';
import { jobTitleLabel } from '../../utils/displayName';
import Pagination, { getPageSize } from '../Pagination';

// Admin → Jobs → Removed jobs: what the server put out of sight, and the way back (server:
// routes/jobs.ts, GET /jobs/removed + POST /jobs/:id/restore). A job is only ever soft-deleted —
// by hand, with its source document, or when its line went off that document — so the row is
// always still on file and Restore is simply the soft delete undone: the job returns to Active or
// Closed Jobs under its own number with its status, amounts, Sales Order and filed documents.
// To make a restored job read as another job's again — a Re-entry Stamp under the extension it
// belongs to — name it in that job's Prerequisite box on Active/Closed Jobs, which can only find
// it once it is back.

const btn = (bg: string, color = '#fff'): React.CSSProperties => ({
  padding: '6px 12px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color,
  fontSize: 12.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
});
const field: React.CSSProperties = {
  padding: '6px 8px', fontSize: 12.5, borderRadius: 8, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none', minWidth: 0,
};
const meta: React.CSSProperties = { fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.5 };

const personOf = (r: RemovedJob): string => [r.first_name, r.last_name].filter(Boolean).join(' ').trim();

export default function RemovedJobs() {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [qLive, setQLive] = useState('');   // the words the list was actually asked for
  const [rows, setRows] = useState<RemovedJob[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [done, setDone] = useState('');
  const run = useRef(0);

  // Only the latest request may fill the list: a slow answer to words already retyped is dropped.
  const load = (words: string, p: number, lim: number) => {
    const mine = ++run.current;
    jobRestoreApi.list(words, p, lim)
      .then(d => { if (mine !== run.current) return; setRows(d.rows || []); setTotal(d.total || 0); })
      .catch((e: any) => { if (mine === run.current) { setRows([]); setErr(e.message); } });
  };
  // Search as you type, 300ms after the last key — and back to page 1, as every other list does.
  useEffect(() => {
    const h = setTimeout(() => { setQLive(q.trim()); setPage(1); }, 300);
    return () => clearTimeout(h);
  }, [q]);
  useEffect(() => { load(qLive, page, limit); }, [qLive, page, limit]);   // eslint-disable-line

  const restore = async (r: RemovedJob) => {
    const title = jobTitleLabel(r.specific_job) || '—';
    const no = fmtNumber(r.job_no);
    if (!window.confirm(r.stand_in
      ? t('jrm_confirm_stand_in', { no, title, other: fmtNumber(r.stand_in) })
      : t('jrm_confirm', { no, title }))) return;
    setBusy(r.id); setErr(''); setDone('');
    try {
      const out = await jobRestoreApi.restore(r.id);
      const said = {
        no: fmtNumber(out.job_no), title: jobTitleLabel(out.specific_job) || '—',
        level: out.process_level, who: out.was_deleted_by_name || t('jrm_by_unknown'),
        when: fmtDateTime(out.was_deleted_at),
      };
      setDone(out.was_deleted_at ? t('jrm_restored', said) : t('jrm_restored_nodate', said));
      load(qLive, page, limit);
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(''); }
  };

  return (
    <div style={{ maxWidth: 820, margin: '16px auto 0' }}>
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16, background: 'var(--bg-surface)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <i className="ti ti-arrow-back-up" style={{ fontSize: 20, color: 'var(--text-secondary)' }} aria-hidden />
          <div style={{ flex: 1, fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{t('jrm_title')}</div>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 12 }}>{t('jrm_desc')}</div>

        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('jrm_search_ph')}
          style={{ ...field, width: '100%', marginBottom: 4 }} />

        {rows === null ? (
          !err && <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" aria-hidden /></div>
        ) : rows.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', padding: '10px 0' }}>{qLive ? t('jrm_no_match') : t('jrm_none')}</div>
        ) : rows.map(r => (
          <div key={r.id} style={{ borderTop: '1px solid var(--border)', padding: '8px 0', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                #{fmtNumber(r.job_no)}{jobTitleLabel(r.specific_job) ? ` · ${jobTitleLabel(r.specific_job)}` : ''}
              </div>
              <div style={meta}>
                {[personOf(r), r.process_level ? `${t('jrm_level')} ${r.process_level}` : '',
                  r.sales_order_no ? `${t('jrm_so')} ${r.sales_order_no}` : '',
                  `${t('jrm_total')} ${moneyTight(r.line_net, 2, r.company_id)}`].filter(Boolean).join(' · ')}
              </div>
              <div style={meta}>
                {r.deleted_at
                  ? t('jrm_removed', { when: fmtDateTime(r.deleted_at), who: r.deleted_by_name || t('jrm_by_unknown') })
                  : t('jrm_removed_nodate', { who: r.deleted_by_name || t('jrm_by_unknown') })}
                {r.delete_reason && <> · <span style={{ color: 'var(--text-secondary)' }}>{t('jrm_reason')} {r.delete_reason}</span></>}
              </div>
              {!!r.stand_in && (
                <div style={{ ...meta, color: '#B45309' }}>
                  <i className="ti ti-alert-triangle" aria-hidden /> {t('jrm_stand_in', { no: fmtNumber(r.stand_in) })}
                </div>
              )}
            </div>
            <button onClick={() => restore(r)} disabled={!!busy}
              style={{ ...btn('var(--accent)'), opacity: busy ? 0.6 : 1 }}>
              <i className="ti ti-arrow-back-up" aria-hidden /> {t('jrm_restore')}
            </button>
          </div>
        ))}

        <Pagination page={page} total={total} limit={limit} onPage={setPage}
          onLimit={n => { setLimit(n); setPage(1); }} />

        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginTop: 10, whiteSpace: 'pre-wrap' }}>{err}</div>}
        {done && <div style={{ fontSize: 12, color: '#15803D', marginTop: 10, lineHeight: 1.5 }}>{done}</div>}
      </div>
    </div>
  );
}
